import moment from 'moment';

import {
  DEFAULT_INTERVAL_MINUTES,
  MINUTES_IN_DAY,
  TIME_OPTION_ID_FORMAT,
  TIME_OPTION_ID_PATTERN,
  TIME_OPTION_LABEL_FORMAT
} from './TimeSelect.constants';

/**
 * @typedef {Object} TimeOption
 * @property {string} id Neutral 24h time, e.g. '14:30'.
 * @property {string} title Display label, e.g. '02:30 PM'.
 */

/**
 * @param {number} [intervalMinutes=15] Must be a positive integer divisor of 1440.
 * @returns {TimeOption[]} Chronologically ordered options covering a full day from '00:00'.
 */
export const generateTimeOptions = (intervalMinutes = DEFAULT_INTERVAL_MINUTES) => {
  const isValidInterval =
    Number.isInteger(intervalMinutes) &&
    intervalMinutes > 0 &&
    MINUTES_IN_DAY % intervalMinutes === 0;

  if (!isValidInterval) {
    throw new Error(
      'intervalMinutes must be a positive integer divisor of 1440 (e.g. 5, 10, 15, 30, 60)'
    );
  }

  const startOfDay = moment.utc().startOf('day');
  const totalSlots = MINUTES_IN_DAY / intervalMinutes;

  return Array.from({ length: totalSlots }, (_, index) => {
    const time = startOfDay.clone().add(index * intervalMinutes, 'minutes');

    return {
      id: time.format(TIME_OPTION_ID_FORMAT),
      title: time.format(TIME_OPTION_LABEL_FORMAT)
    };
  });
};

/**
 * Resolves the nearest option at or after `now`. Callers decide what to do with it:
 * highlight it, or seed it as the field's initial value.
 *
 * Wraps to the first option of the day when no remaining option is available.
 *
 * @param {TimeOption[]} options
 * @param {moment.Moment} [now=moment()]
 * @returns {string|null} The suggested option id, or null when there are no options.
 */
export const getSuggestedOptionId = (options, now = moment()) => {
  if (!options?.length) return null;

  const targetId = now.format(TIME_OPTION_ID_FORMAT);

  const suggestedOption = options.find(option => option.id >= targetId);

  return (suggestedOption || options[0]).id;
};

/**
 * @param {TimeOption[]} options
 * @param {string|null} value
 * @returns {TimeOption|null}
 */
export const getSelectedOption = (options, value) =>
  (value && options.find(option => option.id === value)) || null;

export const isTimeOptionId = value =>
  typeof value === 'string' && TIME_OPTION_ID_PATTERN.test(value);

export const buildTimeOption = id => ({
  id,
  title: moment.utc(id, TIME_OPTION_ID_FORMAT).format(TIME_OPTION_LABEL_FORMAT)
});

export const includeValueOption = (options, value) => {
  if (!isTimeOptionId(value) || options.some(option => option.id === value)) return options;

  return [...options, buildTimeOption(value)].sort((a, b) => a.id.localeCompare(b.id));
};
